/**
 * Main theme scripts
 */

/**
 * Slideshow Component for Announcement Bar
 * Handles auto-rotation and manual navigation of slides
 */
class SlideshowComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('.slider');
    this.slides = this.querySelectorAll('.slideshow__slide');
    this.prevButton = this.querySelector('.slider-button--prev');
    this.nextButton = this.querySelector('.slider-button--next');
    this.currentIndex = 0;
    this.autoplayInterval = null;
    this.isAutoplay = this.slider?.dataset?.autoplay === 'true';
    this.speed = parseInt(this.slider?.dataset?.speed || '5', 10) * 1000;
  }

  connectedCallback() {
    if (this.slides.length <= 1) return;
    
    this.initSlider();
    this.setupEventListeners();
    
    if (this.isAutoplay) {
      this.startAutoplay();
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  initSlider() {
    // Set initial state
    this.slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index !== 0);
      slide.style.display = index === 0 ? 'block' : 'none';
    });
    this.updateAriaLabels();
  }

  setupEventListeners() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        this.stopAutoplay();
        this.prevSlide();
        if (this.isAutoplay) this.startAutoplay();
      });
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        this.stopAutoplay();
        this.nextSlide();
        if (this.isAutoplay) this.startAutoplay();
      });
    }

    // Pause on hover
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => {
      if (this.isAutoplay) this.startAutoplay();
    });

    // Pause on focus for accessibility
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => {
      if (this.isAutoplay) this.startAutoplay();
    });
  }

  goToSlide(index) {
    if (index < 0) index = this.slides.length - 1;
    if (index >= this.slides.length) index = 0;

    // Hide current slide
    this.slides[this.currentIndex].setAttribute('aria-hidden', 'true');
    this.slides[this.currentIndex].style.display = 'none';

    // Show new slide
    this.currentIndex = index;
    this.slides[this.currentIndex].setAttribute('aria-hidden', 'false');
    this.slides[this.currentIndex].style.display = 'block';

    this.updateAriaLabels();
  }

  nextSlide() {
    this.goToSlide(this.currentIndex + 1);
  }

  prevSlide() {
    this.goToSlide(this.currentIndex - 1);
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.speed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  updateAriaLabels() {
    const liveRegion = this.slider;
    if (liveRegion) {
      liveRegion.setAttribute('aria-live', this.autoplayInterval ? 'off' : 'polite');
    }
  }
}

// Register the custom element
if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', SlideshowComponent);
}

/**
 * Collapsible sections (for product page accordions)
 */
document.addEventListener('DOMContentLoaded', function() {
  // Initialize collapsible sections
  const collapsibleTriggers = document.querySelectorAll('.collapsible-section.is-collapsible .collapsible-trigger');
  
  collapsibleTriggers.forEach(trigger => {
    const section = trigger.closest('.collapsible-section');
    const content = section.querySelector('.collapsible-content');
    const icon = trigger.querySelector('.collapsible-icon');
    
    // Default: collapsed state
    if (content) {
      content.style.maxHeight = '0';
      content.style.overflow = 'hidden';
      content.style.transition = 'max-height 0.3s ease';
    }
    
    trigger.addEventListener('click', function() {
      const isOpen = section.classList.contains('is-open');
      
      if (isOpen) {
        section.classList.remove('is-open');
        if (content) content.style.maxHeight = '0';
        if (icon) icon.textContent = '▼';
      } else {
        section.classList.add('is-open');
        if (content) content.style.maxHeight = content.scrollHeight + 'px';
        if (icon) icon.textContent = '▲';
      }
    });
  });
});
